import {
  ReactNode,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation, type TFunction } from 'react-i18next';
import { Button } from '@kizen/kds/Button';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import LegacyButton from '__components/Button';
import type { ChatEvent } from './chatEvents';
import { closeCopy } from './errorCopy';
import { selectCanReconnect, selectExplainedCloseReason } from './reducer';
import { MAX_TEXT_CHARS } from './protocol';
import type { usePiChat } from './usePiChat';
import { PiChatLog, type ToolItem } from './PiChatLog';
import type { ChatPhase } from './PiChatSkeleton';
import { PiChatComposer } from './PiChatComposer';
import {
  ButtonGroup,
  HeaderDivider,
  HeaderName,
  HeaderTitle,
  PanelHeader,
  PanelRoot,
  PanelSection,
  Row,
} from './styles';

/** The confirmation shown when a guarded action would stop a busy AI. */
export interface GuardCopy {
  heading: string;
  body: string;
  confirm: string;
}

/** Runs `action`, first asking with `copy` (the close copy when omitted) while the AI is busy. */
export type GuardBusy = (action: () => void, copy?: GuardCopy) => void;

export const closeChatCopy = (t: TFunction): GuardCopy => ({
  heading: t('Close AI Panel'),
  body: t('The AI is still working. Closing now stops it.'),
  confirm: t('Close'),
});

export interface PiChatPanelProps<E extends ChatEvent> {
  chat: Pick<
    ReturnType<typeof usePiChat>,
    | 'connectionState'
    | 'closedReason'
    | 'closedParams'
    | 'items'
    | 'isStreaming'
    | 'returnedDrafts'
    | 'sendMessage'
    | 'resume'
    | 'addNotice'
    | 'abort'
  >;
  phase: ChatPhase;
  loadingRevealed: boolean;
  canSend: boolean;
  busy: boolean;
  onClose: () => void;
  onReconnect: () => void;
  title: string;
  label: string;
  placeholder: string;
  emptyPrompt: string;
  status: ReactNode;
  actions: (guardBusy: GuardBusy) => ReactNode;
  aboveLog: ReactNode;
  events: E[];
  renderEvent: (event: E) => ReactNode;
  renderToolRow?: (item: ToolItem) => ReactNode;
  groupsTool?: (item: ToolItem) => boolean;
  guardRef?: Ref<GuardBusy>;
  profile?: string;
  className?: string;
}

export const PiChatPanel = <E extends ChatEvent>({
  chat,
  phase,
  loadingRevealed,
  canSend,
  busy,
  onClose,
  onReconnect,
  title,
  label,
  placeholder,
  emptyPrompt,
  status,
  actions,
  aboveLog,
  events,
  renderEvent,
  renderToolRow,
  groupsTool,
  guardRef,
  profile,
  className,
}: PiChatPanelProps<E>) => {
  const { t } = useTranslation();
  const { connectionState, closedReason, isStreaming, sendMessage, addNotice } =
    chat;
  const isLoading = phase === 'loading';
  const explainedCloseReason = selectExplainedCloseReason(chat);
  const closed = closeCopy(closedReason, chat.closedParams, t);

  // Kept after the dialog hides so its copy doesn't blank out while it animates closed.
  const [confirming, setConfirming] = useState<{
    open: boolean;
    action: () => void;
    copy: GuardCopy;
  } | null>(null);
  const guardBusy: GuardBusy = (action, copy = closeChatCopy(t)) => {
    if (busy) {
      setConfirming({ open: true, action, copy });
      return;
    }
    action();
  };
  const dismiss = () =>
    setConfirming((pending) => pending && { ...pending, open: false });
  useImperativeHandle(guardRef, () => guardBusy);

  const handleSendMessage = useCallback(
    async (text: string): Promise<boolean> => {
      const sent = await sendMessage(text);
      if (sent && text.trim().length > MAX_TEXT_CHARS) {
        addNotice(
          t('Message was shortened to {{max}} characters before sending.', {
            max: MAX_TEXT_CHARS,
          })
        );
      }
      return sent;
    },
    [addNotice, sendMessage, t]
  );

  return (
    <PanelRoot
      className={className}
      role="region"
      aria-label={label}
      data-qa-id="pi-chat"
    >
      <PanelHeader>
        <HeaderTitle>
          <Icon icon="text-ai" size="md" color="font-primary" />
          <HeaderName
            size="md"
            weight="semibold"
            color="font-primary"
            truncate
            qa={{ id: 'pi-chat-title' }}
          >
            {title}
          </HeaderName>
          {status}
        </HeaderTitle>
        <ButtonGroup>
          {actions(guardBusy)}
          <HeaderDivider aria-hidden />
          <Button
            variant="text"
            color="tertiary"
            leftIcon="action-close"
            leftIconSettings={{
              size: 'sm',
              tooltipTitleOverride: '',
              tooltipDescriptionOverride: t('Close'),
              tooltipPosition: 'bottom',
            }}
            onClick={() => guardBusy(onClose)}
            qa={{ id: 'pi-chat-close' }}
          />
        </ButtonGroup>
      </PanelHeader>

      {connectionState === 'closed' ? (
        <PanelSection>
          <Row>
            <div className="flex-1 min-w-0">
              <Typography
                size="sm"
                color="font-primary"
                qa={{
                  id: `pi-chat-closed-${explainedCloseReason ?? 'other'}`,
                }}
              >
                {closed.text}
              </Typography>
              {closed.code ? (
                <Typography size="sm" color="font-placeholder" truncate>
                  {closed.code}
                </Typography>
              ) : null}
            </div>
            {selectCanReconnect(chat) ? (
              <Button
                size="small"
                onClick={onReconnect}
                qa={{ id: 'pi-chat-reconnect' }}
              >
                {t('Reconnect')}
              </Button>
            ) : null}
          </Row>
        </PanelSection>
      ) : null}

      {aboveLog}

      <PiChatLog
        phase={phase}
        loadingRevealed={loadingRevealed}
        items={chat.items}
        isStreaming={isStreaming}
        emptyPrompt={emptyPrompt}
        events={events}
        renderEvent={renderEvent}
        renderToolRow={renderToolRow}
        groupsTool={groupsTool}
        profile={profile}
      />

      <PiChatComposer
        placeholder={placeholder}
        disabledMessage={
          isLoading
            ? t('Connecting…')
            : connectionState === 'closed'
              ? t('Not connected')
              : undefined
        }
        canSend={canSend}
        returnedDrafts={chat.returnedDrafts}
        onSend={handleSendMessage}
        onFocus={chat.resume}
        isStreaming={isStreaming}
        onStop={chat.abort}
      />

      <ConfirmationModal
        show={confirming?.open ?? false}
        heading={confirming?.copy.heading}
        onHide={dismiss}
        leftBtn={
          <LegacyButton
            variant="text"
            color="blue"
            data-qa-id="pi-chat-close-cancel"
          />
        }
        defaultLeftBtnText={t('Cancel')}
        actionButton={false}
        rightBtn={
          <LegacyButton color="red" data-qa-id="pi-chat-close-confirm">
            {confirming?.copy.confirm}
          </LegacyButton>
        }
        rightBtnClickHandler={() => {
          if (!confirming?.open) return;
          dismiss();
          confirming.action();
        }}
      >
        {confirming?.copy.body}
      </ConfirmationModal>
    </PanelRoot>
  );
};
